import { apiRequest } from './client'
import type {
  InitMasters,
  SearchJudgmentOverviewParams,
  SearchJudgmentOverviewResponse,
  OfferDetail,
  NgWordMatch,
  NgWordCheckInput,
  JudgeLogRow,
  JudgeOffersRequest,
  JudgeOffersResult,
  SaveMemoRequest,
  UpdateOfferFlagsRequest,
  ApplyRenewalRequest,
  MallCategoryMapping,
  BrowseNodeMasterResult,
  ManageBrowseNodeRequest,
  MallCategoryListResult,
  SaveMallCategoryRequest,
  AddProductImageRequest,
  GetImageUploadUrlRequest,
  GetImageUploadUrlResponse,
  MoveProductImageRequest,
  MoveProductImageResponse,
  RefreshProductImageRequest,
  RefreshProductImageResponse,
  GetProductDetailResponse,
  SaveProductDetailRequest,
  SaveProductDetailResponse,
  GetUsersResult,
  CreateUserRequest,
  UserSummary,
  UpdateUserRequest,
  SetUserStatusRequest,
  RefreshProductFromCatalogRequest,
  RefreshProductFromCatalogResponse,
  SearchImagesByHashResponse,
  SiblingProductsResponse,
  RelatedFileCountsResponse,
  BulkUploadStagingUrlResponse,
  BulkDeleteImagesRequest,
  BulkDeleteImagesResponse,
  BulkReplaceImagesRequest,
  BulkReplaceImagesResponse,
  BulkAddSiblingImagesRequest,
  BulkAddSiblingImagesResponse,
  ApplyJudgmentQueueRequest,
  ApplyJudgmentQueueResponse,
  ProductImagesResponse,
  AmazonImageCandidatesResponse,
  SaveProductImagesRequest,
  SaveProductImagesResponse,
  CreateAiImageEditRequest,
  CreateAiImageEditResponse,
  AiImageEditJob,
  AiImageEditHistoryResponse,
} from '@/types/api'

// 1. GET /masters
export function getInitMasters() {
  return apiRequest<InitMasters>('/masters')
}

// セルモン商品編集 [フェーズ1]: GET /product-detail?asin=...&regionCode=...
export function getProductDetail(asin: string, regionCode: string) {
  return apiRequest<GetProductDetailResponse>('/product-detail', {
    query: { asin, regionCode },
  })
}

// セルモン商品編集 [フェーズ2]: POST /product-detail (保存)
export function saveProductDetail(req: SaveProductDetailRequest) {
  return apiRequest<SaveProductDetailResponse>('/product-detail', {
    method: 'POST',
    body: req,
  })
}

// セルモン商品編集 [フェーズ3]: POST /products/{asin}/refresh-catalog
export function refreshProductFromCatalog(asin: string, req: RefreshProductFromCatalogRequest) {
  return apiRequest<RefreshProductFromCatalogResponse>(
    `/products/${encodeURIComponent(asin)}/refresh-catalog`,
    { method: 'POST', body: req },
  )
}

// 2. GET /offers
export function searchJudgmentOverview(params: SearchJudgmentOverviewParams) {
  return apiRequest<SearchJudgmentOverviewResponse>('/offers', {
    query: {
      countryCode: params.countryCode,
      judgmentStatusCode: params.judgmentStatusCode,
      judgmentSubStatusCode: params.judgmentSubStatusCode,
      sellerId: params.sellerId,
      parentAsin: params.parentAsin,
      childAsin: params.childAsin,
      brand: params.brand,
      productName: params.productName,
      productNameCondition: params.productNameCondition,
      includeDeleted: params.includeDeleted ? 'true' : undefined,
      includeDisabled: params.includeDisabled ? 'true' : undefined,
      memberPlan: params.memberPlan,
      createDateFrom: params.createDateFrom,
      createDateTo: params.createDateTo,
      judgedFilter: params.judgedFilter,
      sortKey: params.sortKey,
      sortDir: params.sortDir,
    },
  })
}

// 3. GET /offers/{asin}
export function getOfferDetail(asin: string, sellerId: string, regionCode = 'jp') {
  return apiRequest<OfferDetail>(`/offers/${encodeURIComponent(asin)}`, {
    query: { sellerId, regionCode },
  })
}

// 4. GET /products/{asin}/images
export function getProductImages(asin: string, regionCode?: string) {
  return apiRequest<ProductImagesResponse>(
    `/products/${encodeURIComponent(asin)}/images`,
    { query: { regionCode } },
  )
}

// 5. POST /ngwords/check
export function checkNgWords(input: NgWordCheckInput) {
  return apiRequest<{ ngWords: NgWordMatch[] }>('/ngwords/check', {
    method: 'POST',
    body: input,
  })
}

// 6. GET /offers/{asin}/logs
export function getJudgeLog(asin: string) {
  return apiRequest<{ asin: string; logs: JudgeLogRow[] }>(
    `/offers/${encodeURIComponent(asin)}/logs`,
  )
}

// 7. POST /offers/judge
export function judgeOffers(req: JudgeOffersRequest) {
  return apiRequest<JudgeOffersResult>('/offers/judge', {
    method: 'POST',
    body: req,
  })
}

// 8. PUT /offers/{asin}/memo
export function saveMemo(asin: string, req: SaveMemoRequest) {
  return apiRequest<{ asin: string; sellerId: string; saved: boolean }>(
    `/offers/${encodeURIComponent(asin)}/memo`,
    { method: 'PUT', body: req },
  )
}

// 9. PUT /offers/{asin}/flags
export function updateOfferFlags(asin: string, req: UpdateOfferFlagsRequest) {
  return apiRequest<{ asin: string; sellerId: string; current: unknown }>(
    `/offers/${encodeURIComponent(asin)}/flags`,
    { method: 'PUT', body: req },
  )
}

// 10. POST /offers/{asin}/renewal
export function applyRenewal(asin: string, req: ApplyRenewalRequest) {
  return apiRequest<{ asin: string; sellerId: string; applied: boolean }>(
    `/offers/${encodeURIComponent(asin)}/renewal`,
    { method: 'POST', body: req },
  )
}

// 11. GET /products/{asin}/categories
export function getMallCategoryMapping(asin: string, regionCode?: string) {
  return apiRequest<MallCategoryMapping>(`/products/${encodeURIComponent(asin)}/categories`, {
    query: { regionCode },
  })
}

// 13a. POST /products/{asin}/images (追加)
export function addProductImage(asin: string, req: AddProductImageRequest) {
  return apiRequest<{ asin: string; imageNo: number; imageUrl: string; added: boolean }>(
    `/products/${encodeURIComponent(asin)}/images`,
    { method: 'POST', body: req },
  )
}

// 13c. POST /products/{asin}/images/upload-url (署名付きアップロードURL発行) [フェーズ2]
export function getImageUploadUrl(asin: string, req: GetImageUploadUrlRequest) {
  return apiRequest<GetImageUploadUrlResponse>(
    `/products/${encodeURIComponent(asin)}/images/upload-url`,
    { method: 'POST', body: req },
  )
}

// 13d. POST /products/{asin}/images/{imageNo}/move (並び替え) [フェーズ3]
export function moveProductImage(asin: string, imageNo: number, req: MoveProductImageRequest) {
  return apiRequest<MoveProductImageResponse>(
    `/products/${encodeURIComponent(asin)}/images/${imageNo}/move`,
    { method: 'POST', body: req },
  )
}

// 13e. POST /products/{asin}/images/refresh (SP-API Catalog Itemsから画像を再取得) [フェーズ5]
export function refreshProductImage(asin: string, req: RefreshProductImageRequest) {
  return apiRequest<RefreshProductImageResponse>(
    `/products/${encodeURIComponent(asin)}/images/refresh`,
    { method: 'POST', body: req },
  )
}

/**
 * 署名付きURLへ実ファイルを直接PUTする(S3への直アップロード) [フェーズ2]。
 * これは Judge-Api(API Gateway)ではなくS3の署名URLへ投げるため、
 * apiRequest(認証ヘッダ付与)は使わず素のfetchを使う。
 * Content-Type は署名時と完全一致させること(getImageUploadUrlのcontentType)。
 */
export async function uploadFileToS3(uploadUrl: string, file: File, contentType: string): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  })
  if (!res.ok) {
    throw new Error(`S3へのアップロードに失敗しました (HTTP ${res.status})`)
  }
}

// 13b. DELETE /products/{asin}/images/{imageNo} (削除)
export function deleteProductImage(
  asin: string,
  imageNo: number,
  regionCode: string,
  imageTypeId?: number,
) {
  return apiRequest<{ asin: string; imageNo: number; deleted: boolean }>(
    `/products/${encodeURIComponent(asin)}/images/${imageNo}`,
    {
      method: 'DELETE',
      query: { regionCode, imageTypeId: imageTypeId?.toString() },
    },
  )
}

// 14. GET /browsenodes  (ブラウズノードマスタ検索: 追加ダイアログ用)
export function getBrowseNodeMaster(keyword?: string, regionCode?: string) {
  return apiRequest<BrowseNodeMasterResult>('/browsenodes', {
    query: { keyword, regionCode },
  })
}

// 15. POST /products/{asin}/browsenodes  (ブラウズノード 追加/削除/兄弟へ反映)
export function manageProductBrowseNode(asin: string, req: ManageBrowseNodeRequest) {
  return apiRequest<{ asin: string; action: string; success: boolean; appliedSiblings?: number }>(
    `/products/${encodeURIComponent(asin)}/browsenodes`,
    { method: 'POST', body: req },
  )
}

// 16. GET /mallcategories  (モールカテゴリマスタ検索: カテゴリ編集ダイアログ用)
export function getMallCategoryList(dbName: string, keyword?: string) {
  return apiRequest<MallCategoryListResult>('/mallcategories', {
    query: { dbName, keyword },
  })
}

// 12(改). PUT /products/{asin}/categories  (カテゴリ割当 保存/削除: CM2/CM3両対応)
export function saveMallCategoryMapping(asin: string, req: SaveMallCategoryRequest) {
  return apiRequest<{ saved?: boolean; deleted?: boolean; scope: string }>(
    `/products/${encodeURIComponent(asin)}/categories`,
    { method: 'PUT', body: req },
  )
}

// ---- 画像一括処理(VB版 ConfirmImageListWindow 相当) --------------------------

// GET /images/similar?asin=...&imageNo=...&regionCode=...&siblingsOnly=true|false
export function searchImagesByHash(
  asin: string,
  imageNo: number,
  regionCode: string,
  siblingsOnly: boolean,
) {
  return apiRequest<SearchImagesByHashResponse>('/images/similar', {
    query: { asin, imageNo: String(imageNo), regionCode, siblingsOnly: String(siblingsOnly) },
  })
}

// GET /related-files/counts?parentAsin=...&childAsin=...&sellerId=...
// 関連ファイル数バッジ用。3件をまとめて1リクエストで取得する
export function getRelatedFileCounts(
  parentAsin: string | null,
  childAsin: string | null,
  sellerId: string | null,
) {
  return apiRequest<RelatedFileCountsResponse>('/related-files/counts', {
    query: {
      parentAsin: parentAsin ?? undefined,
      childAsin: childAsin ?? undefined,
      sellerId: sellerId ?? undefined,
    },
  })
}

// GET /images/sibling-products?asin=...&regionCode=...&parentAsin=...
// 「兄弟ASINへ一括追加」実行前のプレビュー(代表画像・商品名付き兄弟ASIN一覧)
export function getSiblingProducts(asin: string, regionCode: string, parentAsin?: string | null) {
  return apiRequest<SiblingProductsResponse>('/images/sibling-products', {
    query: { asin, regionCode, parentAsin: parentAsin ?? undefined },
  })
}

// POST /images/staging-upload-url (一括処理用の一時アップロードURL発行)
export function getBulkUploadStagingUrl(contentType: string) {
  return apiRequest<BulkUploadStagingUrlResponse>('/images/staging-upload-url', {
    method: 'POST',
    body: { contentType },
  })
}

// POST /images/bulk-delete
export function bulkDeleteImages(req: BulkDeleteImagesRequest) {
  return apiRequest<BulkDeleteImagesResponse>('/images/bulk-delete', {
    method: 'POST',
    body: req,
  })
}

// POST /images/bulk-replace
export function bulkReplaceImages(req: BulkReplaceImagesRequest) {
  return apiRequest<BulkReplaceImagesResponse>('/images/bulk-replace', {
    method: 'POST',
    body: req,
  })
}

// POST /images/bulk-add-siblings
export function bulkAddSiblingImages(req: BulkAddSiblingImagesRequest) {
  return apiRequest<BulkAddSiblingImagesResponse>('/images/bulk-add-siblings', {
    method: 'POST',
    body: req,
  })
}

// ---- ジャッジ待ち申請(VB版 UncheckPicker.exe GUIモード相当) --------------------
export function applyJudgmentQueue(req: ApplyJudgmentQueueRequest) {
  return apiRequest<ApplyJudgmentQueueResponse>('/judgment-queue/apply', {
    method: 'POST',
    body: req,
  })
}

// ---- ユーザ管理(Cognito、Adminグループのみ利用可) ----------------------------
export function getUsers(params: { keyword?: string; group?: string; status?: string }) {
  return apiRequest<GetUsersResult>('/users', { query: params })
}

export function createUser(req: CreateUserRequest) {
  return apiRequest<UserSummary>('/users', { method: 'POST', body: req })
}

export function updateUser(username: string, req: UpdateUserRequest) {
  return apiRequest<UserSummary>(`/users/${encodeURIComponent(username)}`, { method: 'PUT', body: req })
}

export function setUserStatus(username: string, req: SetUserStatusRequest) {
  return apiRequest<{ username: string; enabled: boolean }>(
    `/users/${encodeURIComponent(username)}/status`,
    { method: 'PUT', body: req },
  )
}

// ---- 画像編集(画像編集の作り直し) ---------------------------------------------

// POST /products/{asin}/images/amazon-candidates (Amazonの最新画像を候補として取得。DBは変えない)
export function getAmazonImageCandidates(asin: string, regionCode: string) {
  return apiRequest<AmazonImageCandidatesResponse>(
    `/products/${encodeURIComponent(asin)}/images/amazon-candidates`,
    { method: 'POST', body: { regionCode } },
  )
}

// PUT /products/{asin}/images (画像編集画面の「登録する画像」の並びで一括確定)
export function saveProductImages(asin: string, req: SaveProductImagesRequest) {
  return apiRequest<SaveProductImagesResponse>(`/products/${encodeURIComponent(asin)}/images`, {
    method: 'PUT',
    body: req,
  })
}

// POST /images/ai-edits (AI修正の受付)
export function createAiImageEdit(req: CreateAiImageEditRequest) {
  return apiRequest<CreateAiImageEditResponse>('/images/ai-edits', { method: 'POST', body: req })
}

// GET /images/ai-edits?asin=... (この商品で過去に作ったAI修正の結果)
export function listAiImageEdits(asin: string, regionCode: string) {
  return apiRequest<AiImageEditHistoryResponse>('/images/ai-edits', { query: { asin, regionCode } })
}

// POST /images/ai-edits/hide (候補(AI)の「削除」= 非表示。S3の画像は消さない)
export function hideAiImageEdits(items: { jobId: string; index: number }[]) {
  return apiRequest<{ hidden: number }>('/images/ai-edits/hide', { method: 'POST', body: { items } })
}

// GET /images/ai-edits/{jobId} (AI修正の状態・結果)
export function getAiImageEdit(jobId: string) {
  return apiRequest<AiImageEditJob>(`/images/ai-edits/${encodeURIComponent(jobId)}`)
}

/**
 * 一時置き場へファイルをアップロードし、stagingKey と表示用URLを返す。
 * (getBulkUploadStagingUrl で署名URLを取得 → S3へ直接PUT)
 */
export async function uploadToStaging(file: Blob, contentType: string) {
  const issued = await getBulkUploadStagingUrl(contentType)
  const res = await fetch(issued.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': issued.contentType },
    body: file,
  })
  if (!res.ok) throw new Error(`アップロードに失敗しました (HTTP ${res.status})`)
  return { stagingKey: issued.stagingKey, contentType: issued.contentType }
}
